import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Users, Mail, Copy, RefreshCw, Building2, Link as LinkIcon, QrCode, Download, UserPlus } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../../services/supabase';
import { useBusiness } from '../../../contexts/BusinessContext';
import { useAuth } from '../../../contexts/AuthContext';
import { toast } from 'sonner';
import Modal from '../../../components/common/Modal';
import { QRCodeSVG } from 'qrcode.react';

export function TeamScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { business } = useBusiness();
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [businessLoading, setBusinessLoading] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState('member');
  const [pendingMember, setPendingMember] = useState(null);
  const [processingRequest, setProcessingRequest] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showInviteOptions, setShowInviteOptions] = useState(false);
  const qrRef = useRef();

  // Detecta la página de origen para el botón de retroceso
  const [fromDashboard, setFromDashboard] = useState(false);

  // Función para cargar el negocio desde el localStorage si está disponible
  const loadBusinessFromLocalStorage = () => {
    try {
      const businessData = localStorage.getItem('currentBusiness');
      if (businessData) {
        return JSON.parse(businessData);
      }
    } catch (err) {
      console.error('Error al cargar negocio desde localStorage:', err);
    }
    return null;
  };

  // Guardar el negocio en localStorage
  const saveBusinessToLocalStorage = (businessData) => {
    try {
      localStorage.setItem('currentBusiness', JSON.stringify(businessData));
    } catch (err) {
      console.error('Error al guardar negocio en localStorage:', err);
    }
  };

  // Si no hay business, intentar cargarlo desde localStorage
  const localBusiness = !business?.id ? loadBusinessFromLocalStorage() : null;
  
  // Usar el negocio del contexto o el del localStorage
  const activeBusiness = business?.id ? business : localBusiness;
  
  // Determinar si el usuario es propietario
  const isOwner = activeBusiness?.owner_id === user?.id;
  
  // Generar enlace de invitación
  const BASE_URL = window.location.host.includes('localhost') 
    ? window.location.origin 
    : 'https://tibodigital.com';
  const inviteLink = `${BASE_URL}/i/${activeBusiness?.id}`;

  // Guardar el negocio actual en localStorage cuando cambie
  useEffect(() => {
    if (business?.id) {
      saveBusinessToLocalStorage(business);
    }
  }, [business]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopySuccess(true);
      toast.success('Link copiado al portapapeles');
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Error al copiar:', err);
      toast.error('No se pudo copiar el link');
    }
  };

  const diagnosePermissionsRLS = async () => {
    console.log('=== DIAGNÓSTICO DE PERMISOS RLS ===');
    try {
      // 1. Verificar perfil del usuario actual
      console.log('Verificando perfil del usuario actual...');
      const { data: myProfiles, error: myProfilesError } = await supabase
        .from('profiles')
        .select('*')
        .eq('auth_user_id', user.id);
        
      console.log('Perfiles del usuario actual:', {
        count: myProfiles?.length || 0,
        profiles: myProfiles,
        error: myProfilesError ? {
          message: myProfilesError.message,
          code: myProfilesError.code
        } : null
      });
      
      // 2. Verificar rol del usuario actual para este negocio
      console.log('Verificando rol para este negocio...');
      const { data: myBusinessProfile, error: myBusinessProfileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('auth_user_id', user.id)
        .eq('business_id', activeBusiness.id)
        .single();
        
      console.log('Perfil para este negocio:', {
        profile: myBusinessProfile,
        error: myBusinessProfileError ? {
          message: myBusinessProfileError.message,
          code: myBusinessProfileError.code
        } : null
      });
      
      // 3. Verificar permisos específicos (si existe la función RPC)
      try {
        console.log('Intentando llamar a función RPC de permisos...');
        const { data: permissions, error: permissionsError } = await supabase.rpc(
          'get_current_user_permissions',
          { business_id: activeBusiness.id }
        );
        
        console.log('Respuesta de permisos:', {
          permissions,
          error: permissionsError ? {
            message: permissionsError.message,
            code: permissionsError.code
          } : null
        });
      } catch (rpcError) {
        console.log('La función RPC no existe o no está disponible:', rpcError.message);
      }
      
      // 4. Verificar configuración general de RLS
      console.log('Verificando políticas RLS generales...');
      
      // Intentar leer una fila existente (debería funcionar si hay políticas SELECT)
      const { data: testRead, error: testReadError } = await supabase
        .from('profiles')
        .select('id')
        .limit(1);
        
      console.log('Prueba de lectura:', {
        data: testRead,
        error: testReadError ? {
          message: testReadError.message,
          code: testReadError.code
        } : null
      });
      
      // Intentar insertar un registro temporal y luego borrarlo (probar INSERT/DELETE)
      console.log('Probando inserción...');
      const testId = `test-${Date.now()}`;
      const { data: testInsert, error: testInsertError } = await supabase
        .from('profiles')
        .insert([{
          id: testId,
          auth_user_id: user.id,
          name: 'Test Permission',
          role: 'test'
        }])
        .select();
      
      console.log('Prueba de inserción:', {
        data: testInsert,
        error: testInsertError ? {
          message: testInsertError.message,
          code: testInsertError.code
        } : null
      });
      
      // Si la inserción fue exitosa, eliminar el registro de prueba
      if (testInsert && !testInsertError) {
        const { error: testDeleteError } = await supabase
          .from('profiles')
          .delete()
          .eq('id', testId);
          
        console.log('Prueba de eliminación:', {
          error: testDeleteError ? {
            message: testDeleteError.message,
            code: testDeleteError.code
          } : null
        });
      }
      
    } catch (error) {
      console.error('Error durante diagnóstico:', error);
    }
    console.log('=== FIN DIAGNÓSTICO DE PERMISOS RLS ===');
  };

  const handleApprove = (member) => {
    // Ejecutar diagnóstico de permisos al aprobar
    diagnosePermissionsRLS();
    
    setPendingMember(member);
    setShowRoleModal(true);
  };

  const handleConfirmRole = async () => {
    if (!pendingMember || processingRequest) return;
    
    setProcessingRequest(true);
    try {
      console.log('=== INICIO PROCESO DE APROBACIÓN ===');
      console.log('Detalles de la solicitud:', {
        pendingMember,
        selectedRole,
        currentUser: {
          id: user?.id,
          email: user?.email,
          metadata: user?.user_metadata
        },
        business: {
          id: activeBusiness?.id, 
          name: activeBusiness?.name,
          owner_id: activeBusiness?.owner_id
        },
        isOwner: activeBusiness?.owner_id === user?.id
      });
      
      // 1. Verificar si el usuario ya tiene un perfil general
      console.log('Verificando si existe el perfil del usuario...');
      const { data: existingProfile, error: existingProfileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('auth_user_id', pendingMember.user_id)
        .maybeSingle();
        
      console.log('Resultado de verificación de perfil existente:', {
        perfilExistente: existingProfile,
        error: existingProfileError ? {
          message: existingProfileError.message,
          code: existingProfileError.code
        } : null
      });

      let profileId;
      
      if (existingProfile) {
        console.log('✅ Perfil existente encontrado');
        profileId = existingProfile.id;
      } else {
        // Si el perfil no existe, crearlo
        console.log('Creando nuevo perfil con datos:', {
          auth_user_id: pendingMember.user_id,
          name: pendingMember.name || pendingMember.email.split('@')[0]
        });
        
        const { data: newProfile, error: profileError } = await supabase
          .from('profiles')
          .insert([{
            auth_user_id: pendingMember.user_id,
            name: pendingMember.name || pendingMember.email.split('@')[0]
          }])
          .select()
          .single();

        if (profileError) {
          console.error('Error detallado al crear perfil:', {
            message: profileError.message,
            code: profileError.code,
            details: profileError.details,
            hint: profileError.hint
          });
          throw new Error(`Error al crear perfil: ${profileError.message} (Código: ${profileError.code})`);
        }
        
        console.log('✅ Perfil creado exitosamente:', newProfile);
        profileId = newProfile.id;
      }

      // 2. Verificar si ya es miembro de este negocio
      console.log('Verificando si ya es miembro de este negocio...');
      const { data: existingMember, error: existingMemberError } = await supabase
        .from('business_members')
        .select('*')
        .eq('business_id', activeBusiness.id)
        .eq('profile_id', profileId)
        .maybeSingle();
        
      console.log('Resultado verificación de miembro existente:', {
        miembroExistente: existingMember,
        error: existingMemberError ? {
          message: existingMemberError.message,
          code: existingMemberError.code
        } : null
      });
      
      if (existingMember) {
        console.log('⚠️ El usuario ya es miembro de este negocio');
        toast.info('Este usuario ya es miembro del negocio');
        throw new Error('El usuario ya es miembro de este negocio');
      }

      // 3. Crear entrada en business_members
      console.log('Creando entrada en business_members:', {
        business_id: activeBusiness.id,
        profile_id: profileId,
        role: selectedRole
      });

      const { data: businessMember, error: businessMemberError } = await supabase
        .from('business_members')
        .insert([{
          business_id: activeBusiness.id,
          profile_id: profileId,
          role: selectedRole,
          status: 'active'
        }])
        .select();

      if (businessMemberError) {
        console.error('Error al crear business_member:', {
          message: businessMemberError.message,
          code: businessMemberError.code,
          details: businessMemberError.details
        });
        throw new Error(`Error al crear business_member: ${businessMemberError.message}`);
      }

      console.log('✅ Business member creado exitosamente:', businessMember);

      // 4. Actualizar el estado de la solicitud a 'approved'
      console.log('Actualizando solicitud a estado "approved":', {
        requestId: pendingMember.id,
        businessId: activeBusiness.id,
        userId: pendingMember.user_id
      });
      
      const { data: requestData, error: requestError } = await supabase
        .from('business_join_requests')
        .update({ status: 'approved' })
        .eq('id', pendingMember.id)
        .select();

      if (requestError) {
        console.error('Error detallado al actualizar solicitud:', {
          message: requestError.message,
          code: requestError.code,
          details: requestError.details,
          hint: requestError.hint
        });
        throw new Error(`Error al actualizar solicitud: ${requestError.message} (Código: ${requestError.code})`);
      }
      
      console.log('✅ Solicitud actualizada exitosamente:', requestData);

      // 5. Actualizar la UI
      setPendingRequests(prev => prev.filter(req => req.id !== pendingMember.id));
      toast.success('Miembro aprobado exitosamente');
      
      // 6. Esperar un momento para asegurar que la base de datos se actualice
      console.log('⏳ Esperando para recargar miembros...');
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // 7. Recargar miembros
      console.log('🔄 Recargando lista de miembros...');
      await fetchMembers();
      
      console.log('=== FIN PROCESO DE APROBACIÓN (EXITOSO) ===');

    } catch (error) {
      console.error('Error al aprobar miembro:', {
        message: error.message,
        name: error.name,
        stack: error.stack,
        cause: error.cause
      });
      
      let errorMessage = 'Error al aprobar miembro';
      
      if (error.message) {
        const securityMatch = error.message.match(/security policy/i);
        const constraintMatch = error.message.match(/constraint/i);
        
        if (securityMatch) {
          errorMessage = 'Error de permisos: no tienes autorización para esta acción. Contacta al administrador.';
          console.error('Detectado error de políticas de seguridad RLS. Verificar políticas en Supabase.');
        } else if (constraintMatch) {
          errorMessage = 'Error de restricción en la base de datos. El usuario ya podría tener un perfil.';
        } else {
          errorMessage = `Error: ${error.message}`;
        }
      }
      
      toast.error(errorMessage);
      console.error('=== FIN PROCESO DE APROBACIÓN (FALLIDO) ===');
    } finally {
      setProcessingRequest(false);
      setShowRoleModal(false);
      setPendingMember(null);
      setSelectedRole('member');
    }
  };

  const handleReject = async (member) => {
    if (processingRequest) return;
    
    setProcessingRequest(true);
    try {
      const { error } = await supabase
        .from('business_join_requests')
        .update({ status: 'rejected' })
        .eq('id', member.id);

      if (error) throw error;

      setPendingRequests(prev => prev.filter(req => req.id !== member.id));
      toast.success('Solicitud rechazada');
    } catch (error) {
      console.error('Error al rechazar solicitud:', error);
      toast.error('Error al rechazar solicitud');
    } finally {
      setProcessingRequest(false);
    }
  };

  const fetchMembers = async () => {
    if (!activeBusiness?.id) {
      console.log('❌ No hay business_id disponible');
      return;
    }

    try {
      console.log('=== INICIO FETCHMEMBERS ===');
      console.log('🏢 Business:', {
        id: activeBusiness.id,
        name: activeBusiness.name,
        owner_id: activeBusiness.owner_id,
        raw: activeBusiness
      });
      console.log('👤 Usuario actual:', {
        id: user?.id,
        email: user?.email,
        raw: user
      });
      console.log('🔑 Es owner:', activeBusiness.owner_id === user?.id);
      
      // 1. Obtener el perfil del owner directamente de business_join_requests
      console.log('Buscando perfil del owner en business_join_requests...');
      const { data: ownerRequest, error: ownerRequestError } = await supabase
        .from('business_join_requests')
        .select('user_email, user_name')
        .eq('business_id', activeBusiness.id)
        .eq('user_id', activeBusiness.owner_id)
        .single();

      if (ownerRequestError) {
        console.error('❌ Error obteniendo request del owner:', ownerRequestError);
      } else {
        console.log('✅ Request del owner encontrado:', ownerRequest);
      }

      // 2. Obtener los miembros activos
      console.log('Obteniendo miembros activos...');
      const { data: members, error: membersError } = await supabase
        .from('business_members')
        .select(`
          id,
          role,
          status,
          profiles (
            id,
            auth_user_id,
            name
          )
        `)
        .eq('business_id', activeBusiness.id)
        .eq('status', 'active');

      if (membersError) {
        console.error('❌ Error obteniendo miembros:', membersError);
        throw membersError;
      }

      console.log('✅ Miembros encontrados:', members);

      // 3. Crear la lista final de miembros
      const finalMembers = [];

      // Agregar el owner primero
      if (activeBusiness.owner_id === user?.id) {
        finalMembers.push({
          id: 'owner-' + user.id,
          user_id: user.id,
          name: ownerRequest?.user_name || user.email?.split('@')[0],
          email: user.email,
          role: 'owner',
          status: 'active'
        });
      }

      // Agregar los demás miembros
      members?.forEach(member => {
        // No agregar al owner si ya está en la lista
        if (member.profiles?.auth_user_id !== activeBusiness.owner_id) {
          // Buscar si hay una solicitud de unión para este usuario
          const joinRequest = [...pendingRequests].find(req => 
            req.user_id === member.profiles.auth_user_id && req.status === 'approved'
          );

          finalMembers.push({
            id: member.id,
            user_id: member.profiles.auth_user_id,
            name: member.profiles.name || 'Usuario',
            email: member.profiles.auth_user_id === user.id 
              ? user.email 
              : (joinRequest?.user_email || null),
            role: member.role || 'member',
            status: member.status || 'active'
          });
        }
      });

      console.log('📋 Miembros formateados:', finalMembers);
      setMembers(finalMembers);
      console.log('=== FIN FETCHMEMBERS ===');

    } catch (error) {
      console.error('❌ Error en fetchMembers:', error);
      toast.error('Error al cargar los miembros del equipo');
    }
  };

  const fetchPendingRequests = async () => {
    if (!activeBusiness?.id) {
      console.log('No business ID available');
      return;
    }

    try {
      console.log('=== INICIO FETCHPENDINGREQUESTS ===');
      console.log('Detalles:', {
        businessId: activeBusiness.id,
        userId: user?.id,
        isOwner: activeBusiness.owner_id === user?.id
      });
      
      // Obtener las solicitudes pendientes con la información del usuario
      console.log('Consultando solicitudes pendientes...');
      const { data: requests, error: requestsError } = await supabase
        .from('business_join_requests')
        .select('id, user_id, created_at, status, user_email, user_name')
        .eq('business_id', activeBusiness.id)
        .eq('status', 'pending');

      if (requestsError) {
        console.error('Error fetching requests:', {
          message: requestsError.message,
          code: requestsError.code,
          details: requestsError.details
        });
        throw requestsError;
      }

      console.log('Solicitudes pendientes encontradas:', requests?.length || 0);
      console.log('Detalle de solicitudes pendientes:', requests);

      // Si no hay solicitudes, establecer array vacío
      if (!requests?.length) {
        console.log('No pending requests found');
        setPendingRequests([]);
        return;
      }

      // Formatear los datos (ahora más simple porque ya tenemos toda la info)
      const formattedRequests = requests.map(request => ({
        id: request.id,
        user_id: request.user_id,
        email: request.user_email,
        name: request.user_name,
        created_at: request.created_at
      }));

      console.log('Solicitudes pendientes formateadas:', formattedRequests);
      setPendingRequests(formattedRequests);
      console.log('=== FIN FETCHPENDINGREQUESTS ===');
    } catch (err) {
      console.error('Error in fetchPendingRequests:', {
        message: err.message, 
        code: err.code,
        stack: err.stack
      });
      toast.error('Error al cargar las solicitudes pendientes');
    }
  };

  const handleDownloadQR = () => {
    const canvas = document.createElement("canvas");
    const svg = qrRef.current.querySelector("svg");
    const svgData = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      
      const pngFile = canvas.toDataURL("image/png");
      const downloadLink = document.createElement("a");
      downloadLink.download = `${activeBusiness?.name}-invitacion.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };
    
    img.src = "data:image/svg+xml;base64," + btoa(svgData);
  };

  useEffect(() => {
    console.log('=== DEBUG TEAM SCREEN MOUNT ===');
    console.log('Initial state:', {
      business: {
        id: activeBusiness?.id,
        name: activeBusiness?.name,
        owner_id: activeBusiness?.owner_id
      },
      user: {
        id: user?.id,
        email: user?.email,
        metadata: user?.user_metadata
      },
      isOwner: activeBusiness?.owner_id === user?.id
    });

    // Determinar si venimos del dashboard
    const referrer = document.referrer;
    const isDashboardReferrer = referrer.includes('/dashboard') || location.state?.fromDashboard;
    setFromDashboard(isDashboardReferrer);
    
    const loadData = async () => {
      console.log('=== INICIO CARGA INICIAL DE DATOS ===');
      setLoading(true);
      
      // Solo intentar cargar datos si tenemos un business_id
      if (activeBusiness?.id) {
        console.log('🏢 Business disponible, cargando datos...');
        try {
          console.log('⏳ Cargando solicitudes y miembros...');
          await Promise.all([fetchMembers(), fetchPendingRequests()]);
          console.log('✅ Datos cargados exitosamente');
        } catch (error) {
          console.error('❌ Error en carga inicial:', {
            message: error.message,
            code: error.code
          });
        } finally {
          setLoading(false);
        }
      } else {
        console.log('❌ No hay business_id disponible');
        // Esperar un momento para ver si el business se carga
        console.log('⏳ Esperando 1.5s por si se carga el negocio...');
        setTimeout(async () => {
          if (activeBusiness?.id) {
            console.log('✅ Business cargado después de espera, cargando datos...');
            try {
              await Promise.all([fetchMembers(), fetchPendingRequests()]);
              console.log('✅ Datos cargados exitosamente después de espera');
            } catch (error) {
              console.error('❌ Error en carga después de espera:', {
                message: error.message,
                code: error.code
              });
            }
          } else {
            console.log('❌ Negocio no disponible después de espera');
          }
          setLoading(false);
        }, 1500);
      }
      console.log('=== FIN INICIO CARGA DATOS ===');
    };

    loadData();
  }, [activeBusiness?.id, location.state]);

  // Efecto para recargar miembros cuando cambia la lista de solicitudes pendientes
  useEffect(() => {
    // Solo recargar si ya tenemos activeBusiness y no estamos en carga inicial
    if (activeBusiness?.id && !loading) {
      console.log('🔄 Lista de solicitudes pendientes cambió, recargando miembros...');
      fetchMembers();
    }
  }, [pendingRequests]);

  if (loading) {
    return (
      <div className="flex flex-col gap-6 w-full p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4" />
          <div className="h-12 bg-gray-200 rounded" />
          <div className="space-y-2">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-gray-200 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!activeBusiness?.id) {
    return (
      <div className="flex flex-col gap-6 w-full p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => fromDashboard ? navigate('/dashboard') : navigate('/menu')}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors text-gray-900 dark:text-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Equipo
          </h1>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold mb-2">No se pudo cargar el negocio</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            No se encontró la información del negocio. Por favor, vuelve a intentarlo.
          </p>
          <button 
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-[#3B7C5B] text-white rounded-lg hover:bg-[#2E6346] transition-colors"
          >
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 w-full p-6 pb-24 bg-gray-50 dark:bg-gray-900 min-h-screen relative">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button 
          onClick={() => fromDashboard ? navigate('/dashboard') : navigate('/menu')}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors text-gray-900 dark:text-white"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Equipo
        </h1>
      </div>

      {/* Business Info */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="p-4">
          <div className="flex items-center gap-3">
            <Building2 className="w-6 h-6 text-[#3B7C5B]" />
            <div>
              <h2 className="text-lg font-semibold dark:text-white">
                {activeBusiness?.name}
              </h2>
              {isOwner && (
                <div className="mt-1">
                  <span className="text-xs text-[#3B7C5B] bg-[#3B7C5B]/10 px-2 py-1 rounded">
                    Dueño
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Pending Join Requests - Solo visible para el owner */}
      {isOwner && pendingRequests.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="p-4 border-b dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-[#3B7C5B]" />
                <h2 className="text-lg font-semibold dark:text-white">Solicitudes Pendientes</h2>
              </div>
              <span className="text-sm font-medium text-[#3B7C5B] bg-[#3B7C5B]/10 px-2.5 py-1 rounded-full">
                {pendingRequests.length} {pendingRequests.length === 1 ? 'nueva' : 'nuevas'}
              </span>
            </div>
          </div>
          <div className="divide-y dark:divide-gray-700">
            {pendingRequests.map((request) => (
              <div key={request.id} className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#3B7C5B]/10 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-[#3B7C5B]" />
                    </div>
                    <div>
                      <div className="dark:text-white font-medium">
                        {request.name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {request.email}
                      </div>
                      <div className="text-xs text-[#3B7C5B] mt-1">
                        Solicitud recibida {new Date(request.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleReject(request)}
                      disabled={processingRequest}
                      className="px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                    >
                      Rechazar
                    </button>
                    <button
                      onClick={() => handleApprove(request)}
                      disabled={processingRequest}
                      className="px-3 py-1.5 text-sm font-medium text-white bg-[#3B7C5B] hover:bg-[#2E6346] rounded-lg transition-colors disabled:opacity-50"
                    >
                      Aprobar
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Team Members */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="p-4 border-b dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-gray-400" />
              <h2 className="text-lg font-semibold dark:text-white">Miembros</h2>
            </div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2.5 py-1 rounded-full">
              {members.length}
            </span>
          </div>
        </div>
        <div className="divide-y dark:divide-gray-700">
          {members.map((member) => (
            <div key={member.id} className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-gray-400" />
                <div>
                  <div className="dark:text-white">
                    {member.name}
                  </div>
                  <div className="text-sm text-gray-500">
                    {member.email || (member.user_id ? `ID: ${member.user_id.substring(0, 8)}...` : '')}
                  </div>
                  <div className="text-xs text-[#3B7C5B]">
                    {member.role || 'Miembro'}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Floating Action Button - Solo visible para el owner */}
      {isOwner && (
        <button
          onClick={() => setShowInviteOptions(true)}
          className="fixed bottom-6 right-6 flex items-center gap-2 px-6 py-3 bg-[#3B7C5B] text-white rounded-full shadow-lg hover:bg-[#2E6346] transition-colors"
        >
          <UserPlus className="w-5 h-5" />
          <span className="font-medium">Invitar</span>
        </button>
      )}

      {/* Invite Options Bottom Sheet */}
      <Modal
        isOpen={showInviteOptions}
        onClose={() => setShowInviteOptions(false)}
        position="bottom"
      >
        <div className="p-6 space-y-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Invitar al equipo
            </h3>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Opción de Link */}
            <button
              onClick={() => {
                setShowInviteOptions(false);
                handleCopyLink();
              }}
              className="flex flex-col items-center gap-3 p-4 rounded-lg border-2 border-gray-200 dark:border-gray-700 hover:border-[#3B7C5B] dark:hover:border-[#3B7C5B] transition-colors"
            >
              <div className="w-12 h-12 rounded-full bg-[#3B7C5B]/10 flex items-center justify-center">
                <LinkIcon className="w-6 h-6 text-[#3B7C5B]" />
              </div>
              <span className="text-sm font-medium text-gray-900 dark:text-white">Compartir link</span>
            </button>

            {/* Opción de QR */}
            <button
              onClick={() => {
                setShowInviteOptions(false);
                setShowQRModal(true);
              }}
              className="flex flex-col items-center gap-3 p-4 rounded-lg border-2 border-gray-200 dark:border-gray-700 hover:border-[#3B7C5B] dark:hover:border-[#3B7C5B] transition-colors"
            >
              <div className="w-12 h-12 rounded-full bg-[#3B7C5B]/10 flex items-center justify-center">
                <QrCode className="w-6 h-6 text-[#3B7C5B]" />
              </div>
              <span className="text-sm font-medium text-gray-900 dark:text-white">Mostrar QR</span>
            </button>
          </div>

          <p className="text-sm text-gray-500 dark:text-gray-400 text-center mt-4">
            Cuando los miembros se unan, podrás aprobarlos y asignarles un rol
          </p>
        </div>
      </Modal>

      {/* QR Code Modal */}
      <Modal isOpen={showQRModal} onClose={() => setShowQRModal(false)}>
        <div className="space-y-6 p-2">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white text-center">
            {activeBusiness?.name}
          </h3>
          
          <div ref={qrRef} className="flex justify-center">
            <QRCodeSVG
              value={inviteLink}
              size={280}
              level="H"
              fgColor="#3B7C5B"
              includeMargin={true}
            />
          </div>

          <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
            Pedile a tu equipo que escanee este QR con su cámara y se sumarán a tu equipo
          </p>

          <div className="flex justify-center">
            <button
              onClick={handleDownloadQR}
              className="flex items-center gap-2 px-4 py-2 bg-[#3B7C5B] text-white rounded-lg hover:bg-[#2E6346] transition-colors"
            >
              <Download className="w-4 h-4" />
              Descargar QR
            </button>
          </div>
        </div>
      </Modal>

      {/* Role Selection Modal */}
      <Modal isOpen={showRoleModal} onClose={() => {
        if (!processingRequest) {
          setShowRoleModal(false);
          setPendingMember(null);
          setSelectedRole('member');
        }
      }}>
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Seleccionar Rol
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Selecciona el rol para {pendingMember?.name}
          </p>
          <div className="space-y-2">
            {[
              { value: 'owner', label: 'Dueño', description: 'Control total del negocio' },
              { value: 'member', label: 'Miembro', description: 'Control limitado' }
            ].map(role => (
              <label key={role.value} className="flex items-center p-3 rounded hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer">
                <input
                  type="radio"
                  name="role"
                  value={role.value}
                  checked={selectedRole === role.value}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  className="mr-3"
                  disabled={processingRequest}
                />
                <div>
                  <span className="text-gray-700 dark:text-gray-300">{role.label}</span>
                  <p className="text-xs text-gray-500">{role.description}</p>
                </div>
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <button
              onClick={() => {
                if (!processingRequest) {
                  setShowRoleModal(false);
                  setPendingMember(null);
                  setSelectedRole('member');
                }
              }}
              disabled={processingRequest}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirmRole}
              disabled={processingRequest}
              className="px-4 py-2 text-sm font-medium text-white bg-[#3B7C5B] hover:bg-[#2E6346] rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {processingRequest ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Procesando...
                </>
              ) : (
                'Confirmar'
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
} 